/**
 * Middle pane of a pe3d base: the selected variant, running.
 *
 * The creative is made in the browser from the variant's release
 * (modules/pe3dRelease.js) — the preview network's template with the draft
 * injected — so every edit shows without a build or a save. It is the same
 * file the export produces for that network, which is what the size line
 * measures against the release's delivery limit.
 *
 * When the release has a live template (the preview network with the engine's
 * QA bridge) the frame loads once and edits are sent to the running game
 * (usePe3dLiveFrame); otherwise every edit reloads it. Either way the size
 * line measures the delivery build.
 *
 * Chrome and frame are the snapshot editor's (containerSnapshotPreview.js,
 * toolbarSnapshotPreview.js): a title slot with the size under it, the shared
 * device / zoom controls, icon buttons; the frame at a device's real pixel size
 * scaled into the stage, with right / bottom / corner drag handles
 * (customHooks/useFrameResize.js). Dragging pins the zoom at what is on screen,
 * as there; a device, a typed size or a rotation goes back to fit. The frame
 * runs sandboxed: a playable is third-party-looking code and has no business
 * with the dashboard's storage; it may only open popups, for its store link.
 */

import React, { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import { Button, CircularProgress, IconButton, Popover, Tooltip, Typography } from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import VolumeOffIcon from "@material-ui/icons/VolumeOff";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import GetAppIcon from "@material-ui/icons/GetApp";
import LinkIcon from "@material-ui/icons/Link";
import { BAR_BG, CONTROL_BORDER, FIELD_LABEL, PANEL_BG, SECTION_DIVIDER } from "../snapshotEditor/panelPalette";
import PreviewDeviceControls from "../previewDeviceControls";
import {
  DEFAULT_DEVICE_ID,
  DEVICE_PRESETS,
  STAGE_INSET,
  ZOOM_FIT,
  ZOOM_STEPS,
  clampZoom,
  devicePresetById,
  orientedDeviceSize,
} from "../../modules/previewFrame";
import DialogPe3dShareLink from "./dialogPe3dShareLink";
import { incrementCompanyUsage } from "../../modules/companySubscriptionApi";
import { getServerUri } from "../../modules/uriUtility";
import usePe3dLiveFrame from "../../customHooks/usePe3dLiveFrame";
import useFrameResize from "../../customHooks/useFrameResize";
import DialogPe3dExport, { remainingTests } from "./dialogPe3dExport";
import { exportVariants } from "../../modules/pe3dApi";
import { ERROR_TEXT } from "./pe3dPanelKit";

// Playin's own build: the release ships it as the `default` template and the
// plan counts it as a test rather than a playable.
const TEST_NETWORK = "default";

const PREVIEW_DELAY_MS = 250;
// Scripts, and the store link the playable opens with window.open (Play now,
// the end card) — as a new tab of its own, outside the sandbox, so the store
// page works. Still no same-origin: the playable cannot reach the dashboard.
const FRAME_SANDBOX = "allow-scripts allow-popups allow-popups-to-escape-sandbox";

const useStyles = makeStyles({
  root: { display: "flex", flex: 1, flexDirection: "column", minWidth: 0, minHeight: 0, height: "100%", backgroundColor: PANEL_BG, color: "#e6e6e6" },
  // toolbarSnapshotPreview.js
  toolbar: { display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", minHeight: 40 },
  titleSlot: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", marginRight: 8 },
  title: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, lineHeight: 1.3, opacity: 0.85 },
  size: { fontSize: 11, lineHeight: 1.3, color: FIELD_LABEL, whiteSpace: "nowrap" },
  sizeOver: { color: ERROR_TEXT },
  compactSelect: { fontSize: "12.5px", marginLeft: 4 },
  buttonSlot: { position: "relative", display: "inline-flex" },
  // The two rows of the export menu, sized like the playEngine toolbar's.
  exportChoice: {
    width: "220px",
    justifyContent: "flex-start",
    fontSize: "15px",
    textTransform: "none",
    color: "#e2e5ea",
    padding: "8px 14px",
  },
  buttonSpinner: { position: "absolute", right: 0, top: 0, color: "#8ab4c8" },
  // containerSnapshotPreview.js
  stage: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 10,
    paddingBottom: 30,
    paddingRight: 30,
    minWidth: 0,
    minHeight: 0,
    position: "relative",
    overflow: "auto",
    backgroundColor: PANEL_BG,
  },
  zoomBox: { position: "relative", flexShrink: 0, margin: "auto", boxShadow: `0 0 0 1px ${CONTROL_BORDER}, 0 2px 12px rgba(0, 0, 0, 0.45)` },
  frame: { border: "none", display: "block", transformOrigin: "top left", position: "absolute", top: 0, left: 0, backgroundColor: "#000" },
  handleRight: {
    position: "absolute", top: 0, width: 14, cursor: "ew-resize", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10,
    "& > div": { width: 4, height: "40%", minHeight: 24, background: "#595959", borderRadius: 3, transition: "background 0.15s, height 0.15s" },
    "&:hover > div": { background: "#4eabb1", height: "55%" },
  },
  handleBottom: {
    position: "absolute", left: 0, height: 14, cursor: "ns-resize", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10,
    "& > div": { height: 4, width: "40%", minWidth: 24, background: "#595959", borderRadius: 3, transition: "background 0.15s, width 0.15s" },
    "&:hover > div": { background: "#4eabb1", width: "55%" },
  },
  handleCorner: {
    position: "absolute", width: 20, height: 20, cursor: "nwse-resize", zIndex: 11, display: "flex", alignItems: "center", justifyContent: "center",
    color: "#595959", transition: "color 0.15s", "&:hover": { color: "#4eabb1" },
  },
  sizeOverlay: {
    position: "absolute", transform: "translate(-50%, -50%)", background: "rgba(0,0,0,0.75)", color: "#fff", padding: "6px 14px", borderRadius: 4,
    fontSize: 13, fontFamily: "monospace", pointerEvents: "none", zIndex: 20, letterSpacing: "0.5px", whiteSpace: "nowrap", userSelect: "none",
  },
  overlay: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" },
  placeholder: { opacity: 0.7, fontSize: 14, textAlign: "center", padding: 24 },
  problems: {
    position: "absolute", left: 10, right: 30, bottom: 30, backgroundColor: BAR_BG, border: `1px solid ${SECTION_DIVIDER}`, color: "#ef9a9a",
    fontSize: 12, padding: "6px 10px", borderRadius: 4, maxHeight: "30%", overflowY: "auto", lineHeight: 1.5,
  },
});

async function savePreviewRecord(record) {
  try {
    await fetch(getServerUri("version/savePreviewData"), {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ accessToken: localStorage.getItem("session.accessToken"), ...record }),
    });
  } catch (err) {
    console.error("Failed to save preview data:", err);
  }
}

const megabytes = (bytes) => `${(bytes / 1e6).toFixed(2)} MB`;

export default function ContainerPe3dPreview({ editor, selectedBase, selectedGame, createSnackbar, account, userPermissions, isPortrait: pagePortrait, setIsPortrait: setPagePortrait }) {
  const classes = useStyles();
  const [deviceId, setDeviceId] = useState(DEFAULT_DEVICE_ID);
  // The page may own the orientation (the Design tab edits the half of a
  // portrait/landscape field the preview shows); otherwise it is the preview's own.
  const [ownPortrait, setOwnPortrait] = useState(true);
  const isPortrait = pagePortrait ?? ownPortrait;
  const setIsPortrait = setPagePortrait ?? setOwnPortrait;
  const [zoom, setZoom] = useState(ZOOM_FIT);
  // The game's own, as the export uses (server makeCreatives): the dashboard
  // does not choose a loading screen any more, here or there.
  const loadingScreen = _.get(editor, "release.bundle.baseVariantData.loadingScreen") || "blankLoading";
  const [frame, setFrame] = useState(null); // { url, bytes, maxBytes, problems }
  const [isMaking, setIsMaking] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [stageSize, setStageSize] = useState(null); // { width, height } of the stage, padding included
  const [isExportOpen, setIsExportOpen] = useState(false);
  // Production or test, asked before the export the way the playEngine
  // toolbar asks it (toolbarGamePreview.js).
  const [exportMenuAt, setExportMenuAt] = useState(null);
  const [isTestBuilding, setIsTestBuilding] = useState(false);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const urlRef = useRef(null);
  const iframeRef = useRef(null);
  const resize = useFrameResize();

  const stageRef = useRef(null);

  // Measured, not assumed, and re-attached whenever the stage itself is (re)mounted:
  // there is no stage until a variant is selected, so an observer set up on the
  // first render would watch nothing and leave fit and the drag limit at zero.
  const hasStage = Boolean(editor.selectedID);
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === "undefined") return undefined;
    const measure = () =>
      setStageSize((current) =>
        current && current.width === stage.clientWidth && current.height === stage.clientHeight
          ? current
          : { width: stage.clientWidth, height: stage.clientHeight }
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [hasStage]);

  // The language being edited, resolved (modules/pe3dLocalization.js): switching it only changes what
  // this draft is, so the live frame sends the difference like any edit.
  const { release } = editor;
  const draft = editor.view;
  const live = usePe3dLiveFrame({ release, doc: editor.doc, draft, loadingScreen, reloadKey, iframeRef });

  // The delivery creative a moment after the last edit: the size line and the
  // problems always, the frame itself only when there is no live template.
  useEffect(() => {
    if (!release || !draft) {
      setFrame(null);
      return undefined;
    }
    let cancelled = false;
    setIsMaking(true);
    const timer = setTimeout(async () => {
      try {
        const made = await release.previewHtml(draft, loadingScreen);
        if (cancelled) return;
        let url = null;
        if (!release.hasLive) {
          url = URL.createObjectURL(new Blob([made.html], { type: "text/html" }));
          if (urlRef.current) URL.revokeObjectURL(urlRef.current);
          urlRef.current = url;
        }
        setFrame({ url, bytes: made.bytes, maxBytes: made.maxBytes, problems: made.problems });
      } catch (err) {
        if (!cancelled) setFrame((current) => ({ ...(current || {}), problems: [err.message] }));
      } finally {
        if (!cancelled) setIsMaking(false);
      }
    }, PREVIEW_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [release, draft, loadingScreen]);

  useEffect(() => () => urlRef.current && URL.revokeObjectURL(urlRef.current), []);

  // Sizes are device pixels; STAGE_INSET is the stage's padding plus the handles' room.
  const presetSize = orientedDeviceSize(devicePresetById(deviceId), isPortrait);
  const size = resize.size ? { width: resize.size.w, height: resize.size.h } : presetSize;
  const fitScale = stageSize
    ? clampZoom(Math.min(1, Math.max(1, stageSize.width - STAGE_INSET) / size.width, Math.max(1, stageSize.height - STAGE_INSET) / size.height))
    : 1;
  const scale = zoom === ZOOM_FIT ? fitScale : clampZoom(zoom);
  const boxWidth = Math.floor(size.width * scale);
  const boxHeight = Math.floor(size.height * scale);

  const startResize = (event, edge) => {
    // Dragging under Fit would be a tug of war; pin the zoom at what is on screen.
    if (zoom === ZOOM_FIT) setZoom(scale);
    // The room the stage has now, read off the element rather than the last
    // measurement, converted to device pixels through the zoom.
    const stage = stageRef.current;
    resize.startDrag(event, edge, {
      width: size.width,
      height: size.height,
      scale,
      maxWidth: stage ? Math.max(120, (stage.clientWidth - STAGE_INSET) / scale) : 2048,
      maxHeight: stage ? Math.max(120, (stage.clientHeight - STAGE_INSET) / scale) : 2048,
      onEnd: (final) => setIsPortrait(final.h >= final.w),
    });
  };

  const canExport = !userPermissions || userPermissions.exportPlayable !== false;

  /**
   * Playin's own build of the variant on screen: one file, the `default`
   * template, no dialog — the playEngine toolbar's Test Playable, for pe3d.
   * It spends a test rather than a playable.
   */
  async function downloadTestPlayable() {
    if (!editor.doc) return;
    const left = remainingTests(account);
    if (left <= 0) {
      createSnackbar("Your company's plan allows no more test playables this period", "warning");
      return;
    }

    setIsTestBuilding(true);
    const result = await exportVariants({
      baseID: selectedBase._id,
      variantID: String(editor.selectedID),
      networks: [TEST_NETWORK],
      gameTitle: (selectedGame && selectedGame.title) || "",
    });
    setIsTestBuilding(false);

    if (!result.ok || !_.get(result, "data.file")) {
      createSnackbar("Test playable build failed", "error", false);
      return;
    }
    window.open(result.data.file);
    if (account && account.userCompany) {
      incrementCompanyUsage(account.userCompany._id, { testPlayableCount: 1 })
        .then((company) => account.setUserCompany && account.setUserCompany(company))
        .catch((err) => console.error("Failed to update counter:", err));
    }
  }

  // What a finished link still owes: the preview record and the plan counter.
  // The dialog owns the build and the links themselves.
  const recordDemoLink = ({ file, demoURL }) => {
    savePreviewRecord({
      versionName: String(editor.doc.title),
      siteLink: String(Date.now()),
      amazonLink: file.Location,
      amazonKey: file.Key,
      gameName: (selectedGame && selectedGame.title) || "",
      baseName: selectedBase.title,
      companyID: _.get(account, "userCompany._id", ""),
      demoURL,
    });
    if (account && account.userCompany) {
      incrementCompanyUsage(account.userCompany._id, { testPlayableCount: 1 })
        .then((company) => account.setUserCompany && account.setUserCompany(company))
        .catch((err) => console.error("Failed to update counter:", err));
    }
  };

  if (!editor.selectedID) {
    return (
      <div className={classes.root}>
        <div className={classes.stage}>
          <Typography className={classes.placeholder}>Select a variant to preview it.</Typography>
        </div>
      </div>
    );
  }

  const src = live.isLive ? live.url : frame && frame.url;
  const over = frame && frame.bytes >= frame.maxBytes;
  const problems = [...(live.error ? [`Live preview: ${live.error}`] : []), ...((frame && frame.problems) || [])];
  let message = null;
  if (editor.releaseError) message = `Could not open ${editor.doc ? `${editor.doc.game}@${editor.doc.release}` : "the release"} — ${editor.releaseError}`;

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <div className={classes.titleSlot}>
          <Typography className={classes.title} title={editor.doc ? editor.doc.title : ""}>
            {editor.doc ? editor.doc.title : ""}
          </Typography>
          {frame && frame.bytes > 0 && (
            <Tooltip title="The preview network's file as the export makes it, against the release's delivery limit">
              <span className={`${classes.size} ${over ? classes.sizeOver : ""}`}>
                {megabytes(frame.bytes)} of {(frame.maxBytes / 1e6).toFixed(1)} MB
              </span>
            </Tooltip>
          )}
        </div>

        <PreviewDeviceControls
          isPortrait={isPortrait}
          devices={DEVICE_PRESETS}
          deviceId={deviceId}
          isCustomSize={Boolean(resize.size)}
          previewWidth={size.width}
          previewHeight={size.height}
          zoom={zoom}
          zoomSteps={ZOOM_STEPS}
          effectiveZoom={scale}
          onSelectZoom={setZoom}
          onSelectDevice={(nextId) => {
            resize.setSize(null);
            setZoom(ZOOM_FIT);
            setDeviceId(nextId);
          }}
          onCustomSize={({ width, height }) => {
            resize.setSize({ w: width, h: height });
            setZoom(ZOOM_FIT);
            setIsPortrait(height >= width);
          }}
          onToggleOrientation={() => {
            resize.setSize(null);
            setZoom(ZOOM_FIT);
            setIsPortrait((value) => !value);
          }}
        />

        {live.isLive && (
          <>
            <Tooltip title={live.paused ? "Resume the game" : "Pause the game — edits still show in the still frame"}>
              <IconButton size="small" onClick={() => live.setPaused(!live.paused)}>
                {live.paused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title={live.canMute ? (live.muted ? "Unmute the game" : "Mute the game") : "This release's game cannot be muted from here — publish it with engine 0.1.20 or later"}>
              <span>
                <IconButton size="small" disabled={!live.canMute} onClick={() => live.setMuted(!live.muted)}>
                  {live.muted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
        <Tooltip title="Restart the playable">
          <IconButton size="small" onClick={() => setReloadKey((key) => key + 1)}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canExport && (
          <>
            <Tooltip title="Export this variant">
              <span>
                <IconButton
                  size="small"
                  disabled={!editor.doc || isTestBuilding}
                  onClick={(event) => setExportMenuAt(event.currentTarget)}
                >
                  {isTestBuilding ? <CircularProgress size={16} /> : <GetAppIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Popover
              open={Boolean(exportMenuAt)}
              anchorEl={exportMenuAt}
              onClose={() => setExportMenuAt(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Button
                  className={classes.exportChoice}
                  onClick={() => {
                    setExportMenuAt(null);
                    setIsExportOpen(true);
                  }}
                >
                  <GetAppIcon fontSize="small" style={{ marginRight: 6 }} />
                  Production Playable
                </Button>
                <Button
                  className={classes.exportChoice}
                  onClick={() => {
                    setExportMenuAt(null);
                    void downloadTestPlayable();
                  }}
                >
                  <GetAppIcon fontSize="small" style={{ marginRight: 6 }} />
                  Test Playable
                </Button>
              </div>
            </Popover>
            <Tooltip title="Make a demo link to share">
              <span className={classes.buttonSlot}>
                <IconButton size="small" disabled={!editor.doc} onClick={() => setIsLinkOpen(true)}>
                  <LinkIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </div>

      <div ref={stageRef} className={classes.stage}>
        {message && <Typography className={classes.placeholder} style={{ color: "#ef9a9a" }}>{message}</Typography>}
        {!message && release && (
          <div className={classes.zoomBox} style={{ width: boxWidth, height: boxHeight }}>
            {src && (
              <iframe
                ref={iframeRef}
                key={live.isLive ? live.url : `${frame.url}-${reloadKey}`}
                title="pe3d preview"
                className={classes.frame}
                src={src}
                sandbox={FRAME_SANDBOX}
                style={{ width: size.width, height: size.height, transform: `scale(${scale})` }}
              />
            )}
            <div className={classes.handleRight} style={{ left: boxWidth + 6, height: boxHeight }} onPointerDown={(event) => startResize(event, "right")}>
              <div />
            </div>
            <div className={classes.handleBottom} style={{ top: boxHeight + 6, width: boxWidth }} onPointerDown={(event) => startResize(event, "bottom")}>
              <div />
            </div>
            <div className={classes.handleCorner} style={{ left: boxWidth, top: boxHeight }} onPointerDown={(event) => startResize(event, "corner")}>
              <svg viewBox="0 0 10 10" width="10" height="10" style={{ display: "block", pointerEvents: "none" }}>
                <line x1="1" y1="9" x2="9" y2="1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="5" y1="9" x2="9" y2="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            {resize.isDragging && (
              <div className={classes.sizeOverlay} style={{ left: Math.round(boxWidth / 2), top: Math.round(boxHeight / 2) }}>
                {size.width} × {size.height}
              </div>
            )}
          </div>
        )}
        {!message && (!editor.doc || !release || (!src && isMaking)) && (
          <div className={classes.overlay}>
            <CircularProgress size={28} />
          </div>
        )}
        {problems.length > 0 && (
          <div className={classes.problems}>
            {problems.map((problem) => (
              <div key={problem}>{problem}</div>
            ))}
          </div>
        )}
      </div>
      <DialogPe3dExport
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        editor={editor}
        selectedBase={selectedBase}
        selectedGame={selectedGame}
        createSnackbar={createSnackbar}
        account={account}
        initialIDs={editor.selectedID ? [String(editor.selectedID)] : []}
      />
      <DialogPe3dShareLink
        open={isLinkOpen}
        onClose={() => setIsLinkOpen(false)}
        editor={editor}
        selectedBase={selectedBase}
        createSnackbar={createSnackbar}
        onLinkMade={recordDemoLink}
      />
    </div>
  );
}
